import { Children, type FormEvent, Fragment, PureComponent, type ReactElement, type ReactNode } from "react";

import { occUrl, occFetch } from "~/app/api/occ/occFetch";
import { MAX_TEXT_LENGTH_CONTACT_FORMS } from "~/utils/constants";

import { trackCustomerInquiry } from "~/app/analytics/ga/ga";
import LabeledInput from "~/common/components/LabeledInput";
import { type WithTranslation, withTranslation as withTranslationBase } from "react-i18next";
import { HOMEPAGE } from "~/utils/url/staticUrls";
import { Card } from "~/features/configurator/Linear/components/Card";
import { SimpleSelect } from "~/common/components/Select/SimpleSelect";
import { Button, TextField } from "~/common/mui";
import { Alert } from "~/common/mui/Alert/Alert";
import { type ContactUsDisplayProps, type ContactUsSubmissionResponse } from "~/features/contactUs/types";
import { validateEmail } from "~/utils/emailValidator";
import { moxyStringMapToRecord, type MoxyStringMapDTO } from "~/common/types/MoxyMapDTO";

interface ContactUsFormProps extends WithTranslation, Omit<ContactUsDisplayProps, "subjects"> {
  subjects: {
    label: string;
    value: string;
  }[];
  onSubmitSuccess: (response: ContactUsSubmissionResponse) => void;
}

interface ContactUsFormState {
  message: string;
  name: string;
  surname: string;
  email: string;
  phone: string;
  selectedTopic?: string;
  selectedTitle?: string;
  selectedSalutation?: string;
  validationErrors: Partial<ValidationErrorFields>;
  serverError?: string;
  isSubmitting: boolean;
}

enum ValidationFields {
  SELECTED_TOPIC,
  MESSAGE,
  NAME,
  SURNAME,
  EMAIL,
}

type ValidationErrorFields = { [key in ValidationFields]: string };

interface ContactUsValidationErrorResponse {
  errors?: MoxyStringMapDTO;
}

interface ContactUsRedirectResponse {
  redirectUrl?: string;
}

const FormControl = (props: { children?: ReactNode }): ReactNode => {
  return Children.map(props.children, (child) => {
    // not to add margin on labels
    if ((child as ReactElement).type === LabeledInput) {
      return child;
    } else {
      return <div className="mb-3">{child}</div>;
    }
  });
};

class ContactUsForm extends PureComponent<ContactUsFormProps, ContactUsFormState> {
  constructor(props: ContactUsFormProps) {
    super(props);

    this.state = {
      message: props.message ?? "",
      name: props.user.firstName ?? "",
      surname: props.user.lastName ?? "",
      email: props.user.email ?? "",
      phone: props.user.phone ?? "",
      selectedTopic: props.selectedSubject ?? "",
      selectedTitle: props.user.title ?? "",
      selectedSalutation: props.user.salutation ?? "",
      validationErrors: {},
      serverError: undefined,
      isSubmitting: false,
    };

    this.validate = this.validate.bind(this);
  }

  async validate(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const { t } = this.props;

    const validationErrors: Partial<ValidationErrorFields> = {};

    if (!this.state.selectedTopic) {
      validationErrors[ValidationFields.SELECTED_TOPIC] = t("page.contactUs.form.error.subjectValue.notEmpty");
    }
    if (!this.state.message) {
      validationErrors[ValidationFields.MESSAGE] = t("page.contactUs.form.error.message.notEmpty");
    }
    if (!this.state.name) {
      validationErrors[ValidationFields.NAME] = t("page.contactUs.form.error.firstName.notEmpty");
    }
    if (!this.state.surname) {
      validationErrors[ValidationFields.SURNAME] = t("page.contactUs.form.error.lastName.notEmpty");
    }

    const emailValidation = validateEmail(
      t("page.contactUs.form.error.email.pattern"),
      t("page.contactUs.form.error.email.pattern"),
    )(this.state.email);

    if (emailValidation !== true) {
      validationErrors[ValidationFields.EMAIL] = emailValidation;
    }

    this.setState({ validationErrors: validationErrors, serverError: undefined });

    if (Object.keys(validationErrors).length === 0) {
      trackCustomerInquiry(this.state.selectedTopic!);
      await this.submitInquiry();
    }
  }

  private async submitInquiry() {
    const { t, onSubmitSuccess } = this.props;
    const { message, name, surname, email, phone, selectedTopic, selectedSalutation, selectedTitle } = this.state;
    const selectedTopicTranslation = selectedTopic ? t(selectedTopic) ?? "" : "";
    let shouldResetSubmitting = true;

    this.setState({ isSubmitting: true });

    try {
      const response = await occFetch(occUrl("/contact-us"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          subjectValue: selectedTopic,
          subjectTranslated: selectedTopicTranslation,
          message,
          salutation: selectedSalutation,
          title: selectedTitle,
          firstName: name,
          lastName: surname,
          email,
          phone,
        }),
      });

      const responseBody = await readJsonResponse<
        ContactUsSubmissionResponse | ContactUsValidationErrorResponse | ContactUsRedirectResponse
      >(response);

      if (response.ok) {
        shouldResetSubmitting = false;
        onSubmitSuccess(responseBody as ContactUsSubmissionResponse);
        return;
      }

      if (isRedirectResponse(responseBody)) {
        shouldResetSubmitting = false;
        window.location.href = responseBody.redirectUrl;
        return;
      }

      this.setState({
        validationErrors: mapServerErrors(responseBody),
        serverError: isValidationErrorResponse(responseBody) ? undefined : "generic.error.ajax",
      });
    } catch (e) {
      console.error(e);
      this.setState({ serverError: "generic.error.ajax" });
    } finally {
      if (shouldResetSubmitting) {
        this.setState({ isSubmitting: false });
      }
    }
  }

  render() {
    const { t, subjects, salutations, titles } = this.props;
    const {
      message,
      name,
      surname,
      email,
      phone,
      selectedTopic,
      selectedSalutation,
      selectedTitle,
      validationErrors,
      serverError,
      isSubmitting,
    } = this.state;

    const ffPrefix = "contactUsPage.form.fields";

    const remodeledTitlesArray = titles.map((obj) => {
      const labelTranslated = t(obj.label as string);
      return { value: obj.value, label: labelTranslated };
    });

    const remodeledSalutationsArray = salutations.map((obj) => {
      const labelTranslated = t(obj.label as string);
      return { value: obj.value, label: labelTranslated };
    });

    return (
      <Fragment>
        <Card className={"p-30"} disableHover={true}>
          {serverError && (
            <Alert severity="error" className="mb-3">
              {t(serverError)}
            </Alert>
          )}
          <form className="scha__contact-us-form" onSubmit={this.validate}>
            <div className="row">
              <div className="col-6">
                <FormControl>
                  <SimpleSelect
                    className={"mb-3"}
                    shrink={true}
                    values={subjects}
                    label={t(`${ffPrefix}.subject.label`)}
                    id={"contact_subject_dropdown"}
                    value={selectedTopic}
                    onChange={(e) => this.setState({ selectedTopic: e.target.value as string })}
                    placeholder={t(`${ffPrefix}.subject.placeholder`)}
                    error={!!validationErrors[ValidationFields.SELECTED_TOPIC]}
                    fullWidth={true}
                    helperText={validationErrors[ValidationFields.SELECTED_TOPIC]}
                  />
                  <TextField
                    variant={"outlined"}
                    multiline
                    fullWidth
                    inputProps={{ sx: { height: "200px !important" }, maxLength: MAX_TEXT_LENGTH_CONTACT_FORMS }}
                    label={t(`${ffPrefix}.message.label`)}
                    value={message}
                    id={"contact_message_field"}
                    className={`scha__contact-us-form__message`}
                    onChange={(e) => this.setState({ message: e.target.value })}
                    placeholder={t(`${ffPrefix}.message.placeholder`)}
                    error={!!validationErrors[ValidationFields.MESSAGE]}
                    helperText={validationErrors[ValidationFields.MESSAGE]}
                  />
                </FormControl>
              </div>

              <div className="col-6">
                <FormControl>
                  <SimpleSelect
                    shrink={true}
                    fullWidth={true}
                    label={t(`${ffPrefix}.title.label`)}
                    className="mb-3"
                    values={remodeledTitlesArray}
                    id={"contact_title_dropdown"}
                    value={selectedTitle}
                    onChange={(e) => this.setState({ selectedTitle: e.target.value as string })}
                    placeholder={t(`${ffPrefix}.title.placeholder`)}
                  />
                  <SimpleSelect
                    shrink={true}
                    fullWidth={true}
                    label={t(`${ffPrefix}.salutation.label`)}
                    className="mb-3"
                    values={remodeledSalutationsArray}
                    id={"contact_salution_dropdown"}
                    value={selectedSalutation}
                    onChange={(e) => this.setState({ selectedSalutation: e.target.value as string })}
                    placeholder={t(`${ffPrefix}.salutation.placeholder`)}
                  />
                  <TextField
                    fullWidth
                    value={name}
                    id={"contact_name_field"}
                    onChange={(e) => this.setState({ name: e.target.value })}
                    error={!!validationErrors[ValidationFields.NAME]}
                    helperText={validationErrors[ValidationFields.NAME]}
                    label={t(`${ffPrefix}.name.label`)}
                  />
                  <TextField
                    fullWidth
                    value={surname}
                    id={"contact_surname_field"}
                    onChange={(e) => this.setState({ surname: e.target.value })}
                    name="lastName"
                    error={!!validationErrors[ValidationFields.SURNAME]}
                    helperText={validationErrors[ValidationFields.SURNAME]}
                    label={t(`${ffPrefix}.surname.label`)}
                  />
                  <TextField
                    fullWidth
                    value={email}
                    id={"contact_email_field"}
                    onChange={(e) => this.setState({ email: e.target.value })}
                    error={!!validationErrors[ValidationFields.EMAIL]}
                    helperText={validationErrors[ValidationFields.EMAIL]}
                    label={t(`${ffPrefix}.email.label`)}
                  />
                  <TextField
                    fullWidth
                    value={phone}
                    id={"contact_phone_field"}
                    onChange={(e) => this.setState({ phone: e.target.value })}
                    label={t(`${ffPrefix}.phone.label`)}
                  />
                </FormControl>
              </div>

              <div className="col-12 mt-3">
                <div className="d-flex flex-justify-between flex-align-center">
                  <span className="flex-2">{t("contactUsPage.form.mandatory")}</span>
                  <div className="d-flex flex-1">
                    <Button
                      color={"secondary"}
                      className="scha__contact-us-form__button"
                      id={"contact_cancel_btn"}
                      href={HOMEPAGE}
                      fullWidth={true}
                    >
                      {t("contactUsPage.form.cancel")}
                    </Button>
                    <Button
                      className="scha__contact-us-form__button ml-4"
                      id={"contact_send_btn"}
                      type="submit"
                      fullWidth={true}
                      disabled={isSubmitting}
                    >
                      {t("contactUsPage.form.ok")}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </Card>
      </Fragment>
    );
  }
}

async function readJsonResponse<T>(response: Response): Promise<T> {
  const text = await response.text();
  return text ? (JSON.parse(text) as T) : ({} as T);
}

function isRedirectResponse(response: unknown): response is Required<ContactUsRedirectResponse> {
  return (
    typeof response === "object" &&
    response !== null &&
    "redirectUrl" in response &&
    typeof (response as ContactUsRedirectResponse).redirectUrl === "string"
  );
}

function isValidationErrorResponse(response: unknown): response is Required<ContactUsValidationErrorResponse> {
  return (
    typeof response === "object" &&
    response !== null &&
    "errors" in response &&
    typeof (response as ContactUsValidationErrorResponse).errors === "object"
  );
}

function mapServerErrors(response: unknown): Partial<ValidationErrorFields> {
  return Object.entries(normalizeContactUsValidationErrors(response)).reduce<Partial<ValidationErrorFields>>(
    (errors, [field, message]) => {
      const validationField = SERVER_FIELD_MAP[field];
      if (validationField !== undefined && message) {
        errors[validationField] = message;
      }
      return errors;
    },
    {},
  );
}

export function normalizeContactUsValidationErrors(response: unknown): Record<string, string> {
  return isValidationErrorResponse(response) ? moxyStringMapToRecord(response.errors) : {};
}

const SERVER_FIELD_MAP: Record<string, ValidationFields> = {
  subjectValue: ValidationFields.SELECTED_TOPIC,
  subjectTranslated: ValidationFields.SELECTED_TOPIC,
  message: ValidationFields.MESSAGE,
  firstName: ValidationFields.NAME,
  lastName: ValidationFields.SURNAME,
  email: ValidationFields.EMAIL,
};

export default withTranslationBase()(ContactUsForm);
